import React, { useState, useEffect } from "react";
import {
    TrendingUp,
    Calendar,
    Clock,
    Target,
    AlertCircle,
    Award,
    Zap,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    Download,
    Lightbulb,
    BarChart3,
    Activity,
    Users,
} from "lucide-react";
import { db } from "../../firebase";
import {
    collection,
    query,
    where,
    getDocs,
    Timestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    Area,
    Cell,
} from "recharts";

const DIAS_SEMANA = ["LUNES", "MARTES", "MIÉRCOLES", "JUEVES", "VIERNES", "SÁBADO", "DOMINGO"];

const SUCURSALES = [
    "OLIVARES", "REFORMA", "EXPRESS", "MARIACHI", "VERBENA", "VILDOSOLA",
    "LIBERTAD", "GUEVARA", "NVO. HILLO", "QUIROGA", "PROGRESO", "CARRETA"
];

const COLORS = {
    primary: "#FF0000",
    success: "#4CAF50",
    warning: "#FFA500",
    danger: "#DC2626",
    info: "#2196F3",
    purple: "#9C27B0",
};

const AnalisisAvanzado = () => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [yearData, setYearData] = useState([]);

    // Analytics por día de la semana
    const [weekdayAnalysis, setWeekdayAnalysis] = useState([]);

    // Analytics estacional
    const [seasonalAnalysis, setSeasonalAnalysis] = useState([]);

    // Analytics por sucursal
    const [branchAnalysis, setBranchAnalysis] = useState([]);

    // Recomendaciones
    const [recommendations, setRecommendations] = useState([]);

    // Heatmap data
    const [heatmapData, setHeatmapData] = useState([]);

    // Comparación de días
    const [bestWorstDays, setBestWorstDays] = useState({ best: [], worst: [] });

    useEffect(() => {
        fetchYearData();
    }, [selectedYear]);

    const fetchYearData = async () => {
        setLoading(true);
        try {
            const startOfYear = Timestamp.fromDate(new Date(selectedYear, 0, 1));
            const endOfYear = Timestamp.fromDate(new Date(selectedYear, 11, 31, 23, 59, 59));

            const q = query(
                collection(db, "inventario"),
                where("fecha", ">=", startOfYear),
                where("fecha", "<=", endOfYear)
            );

            const snapshot = await getDocs(q);
            const data = [];
            snapshot.forEach((doc) => {
                data.push({ id: doc.id, ...doc.data() });
            });

            data.sort((a, b) => a.fecha.toMillis() - b.fecha.toMillis());

            setYearData(data);

            // Calcular todos los análisis
            analyzeByWeekday(data);
            analyzeBySeason(data);
            analyzeByBranch(data);
            generateRecommendations(data);
            generateHeatmap(data);
            findBestWorstDays(data);
        } catch (error) {
            console.error("Error cargando datos:", error);
        } finally {
            setLoading(false);
        }
    };

    // ANÁLISIS POR DÍA DE LA SEMANA
    const analyzeByWeekday = (data) => {
        const porDia = {};

        // Inicializar
        DIAS_SEMANA.forEach(dia => {
            porDia[dia] = {
                dia,
                totalVentas: 0,
                totalDias: 0,
                promedio: 0,
                devoluciones: 0,
                tasaDevolucion: 0,
            };
        });

        // Agregar datos
        data.forEach(item => {
            const dia = item.diaSemana || "LUNES";
            if (porDia[dia]) {
                porDia[dia].totalVentas += item.salidaTotal || 0;
                porDia[dia].totalDias += 1;
                porDia[dia].devoluciones += item.devolucion || 0;
            }
        });

        // Calcular promedios
        const analysis = DIAS_SEMANA.map(dia => {
            const datos = porDia[dia];
            const promedio = datos.totalDias > 0
                ? Math.round(datos.totalVentas / datos.totalDias)
                : 0;
            const tasaDevolucion = datos.totalVentas > 0
                ? ((datos.devoluciones / datos.totalVentas) * 100).toFixed(2)
                : 0;

            return {
                ...datos,
                promedio,
                tasaDevolucion: parseFloat(tasaDevolucion),
            };
        });

        setWeekdayAnalysis(analysis);
    };

    // ANÁLISIS ESTACIONAL (por semanas del año)
    const analyzeBySeason = (data) => {
        const porSemana = {};

        data.forEach(item => {
            const date = item.fecha.toDate();
            const weekNumber = getWeekNumber(date);

            if (!porSemana[weekNumber]) {
                porSemana[weekNumber] = {
                    semana: weekNumber,
                    ventas: 0,
                    dias: 0,
                };
            }

            porSemana[weekNumber].ventas += item.salidaTotal || 0;
            porSemana[weekNumber].dias += 1;
        });

        const seasonal = Object.values(porSemana).map(s => ({
            ...s,
            promedio: s.dias > 0 ? Math.round(s.ventas / s.dias) : 0,
        }));

        setSeasonalAnalysis(seasonal);
    };

    // ANÁLISIS POR SUCURSAL Y DÍA
    const analyzeByBranch = (data) => {
        const porSucursal = {};

        // Inicializar
        SUCURSALES.forEach(suc => {
            porSucursal[suc] = {
                nombre: suc,
                porDia: {},
                total: 0,
            };

            DIAS_SEMANA.forEach(dia => {
                porSucursal[suc].porDia[dia] = {
                    ventas: 0,
                    dias: 0,
                    promedio: 0,
                };
            });
        });

        // Agregar datos
        data.forEach(item => {
            const dia = item.diaSemana || "LUNES";

            SUCURSALES.forEach(suc => {
                if (item.sucursales && item.sucursales[suc]) {
                    const ventas = item.sucursales[suc];
                    porSucursal[suc].porDia[dia].ventas += ventas;
                    porSucursal[suc].porDia[dia].dias += 1;
                    porSucursal[suc].total += ventas;
                }
            });
        });

        // Calcular promedios
        const analysis = SUCURSALES.map(suc => {
            const datos = porSucursal[suc];

            const porDiaArray = DIAS_SEMANA.map(dia => {
                const diaData = datos.porDia[dia];
                return {
                    dia,
                    promedio: diaData.dias > 0
                        ? Math.round(diaData.ventas / diaData.dias)
                        : 0,
                };
            });

            // Encontrar mejor y peor día
            const mejorDia = porDiaArray.reduce((max, d) =>
                d.promedio > max.promedio ? d : max, porDiaArray[0]);
            const peorDia = porDiaArray.reduce((min, d) =>
                d.promedio < min.promedio ? d : min, porDiaArray[0]);

            return {
                nombre: suc,
                total: datos.total,
                porDia: porDiaArray,
                mejorDia: mejorDia.dia,
                peorDia: peorDia.dia,
                variabilidad: calcularVariabilidad(porDiaArray),
            };
        });

        setBranchAnalysis(analysis.sort((a, b) => b.total - a.total));
    };

    // GENERAR RECOMENDACIONES INTELIGENTES
    const generateRecommendations = (data) => {
        const recos = [];

        if (!data || data.length === 0) {
            setRecommendations([]);
            return;
        }

        // Análisis por día de la semana
        const porDia = {};
        DIAS_SEMANA.forEach(dia => {
            porDia[dia] = { ventas: 0, dias: 0 };
        });

        data.forEach(item => {
            const dia = item.diaSemana || "LUNES";
            if (porDia[dia]) {
                porDia[dia].ventas += item.salidaTotal || 0;
                porDia[dia].dias += 1;
            }
        });

        const promedios = DIAS_SEMANA.map(dia => ({
            dia,
            promedio: porDia[dia].dias > 0
                ? porDia[dia].ventas / porDia[dia].dias
                : 0,
        }));

        // Encontrar días débiles (< 80% del promedio general)
        const promedioGeneral = promedios.reduce((sum, d) => sum + d.promedio, 0) / 7;
        const diasDebiles = promedios.filter(d => d.promedio < promedioGeneral * 0.8);

        if (diasDebiles.length > 0) {
            recos.push({
                tipo: "promocion",
                prioridad: "alta",
                titulo: "🎯 Días Ideales para Promociones",
                descripcion: `${diasDebiles.map(d => d.dia).join(", ")} tienen MENOS salidas (ventas más bajas). Lanza promociones estos días para aumentar ventas.`,
                impacto: "Potencial aumento del 15-25% en ventas en días débiles",
                icon: "target",
                color: "orange",
            });
        }

        // Encontrar días fuertes (MÁS SALIDAS = MEJORES DÍAS)
        const diasFuertes = promedios.filter(d => d.promedio > promedioGeneral * 1.2);
        if (diasFuertes.length > 0) {
            recos.push({
                tipo: "optimizacion",
                prioridad: "alta",
                titulo: "⚡ Maximizar Días con Más Ventas",
                descripcion: `${diasFuertes.map(d => d.dia).join(", ")} son tus MEJORES días con MÁS SALIDAS. Aumenta inventario y personal para no perder ventas.`,
                impacto: "Evitar pérdida de ventas por falta de stock en días pico",
                icon: "zap",
                color: "green",
            });
        }

        // Análisis de devoluciones
        const totalDevoluciones = data.reduce((sum, item) => sum + (item.devolucion || 0), 0);
        const totalVentas = data.reduce((sum, item) => sum + (item.salidaTotal || 0), 0);
        const tasaDevolucion = totalVentas > 0 ? (totalDevoluciones / totalVentas) * 100 : 0;

        if (tasaDevolucion > 5) {
            recos.push({
                tipo: "calidad",
                prioridad: "alta",
                titulo: "Reducir Devoluciones",
                descripcion: `Tasa de devolución de ${tasaDevolucion.toFixed(1)}% está por encima del objetivo (5%).`,
                impacto: `Ahorro potencial de ${Math.round(totalDevoluciones * 0.3)} unidades`,
                icon: "alert",
                color: "red",
            });
        }

        // Análisis de temporadas
        if (seasonalAnalysis.length > 0) {
            const semanasOrdenadas = [...seasonalAnalysis].sort((a, b) => b.promedio - a.promedio);
            const mejoresTemporadas = semanasOrdenadas.slice(0, 4);

            if (mejoresTemporadas.length > 0) {
                recos.push({
                    tipo: "planificacion",
                    prioridad: "media",
                    titulo: "Preparación Temporadas Altas",
                    descripcion: `Semanas ${mejoresTemporadas.map(s => s.semana).join(", ")} son las más fuertes del año.`,
                    impacto: "Aumentar stock 30% antes de estas semanas",
                    icon: "calendar",
                    color: "blue",
                });
            }
        }

        // Análisis de sucursales
        const sucursalDebil = branchAnalysis[branchAnalysis.length - 1];
        const sucursalFuerte = branchAnalysis[0];

        if (sucursalDebil && sucursalFuerte) {
            const diferencia = sucursalFuerte.total - sucursalDebil.total;
            const porcentajeDif = (diferencia / sucursalFuerte.total) * 100;

            if (porcentajeDif > 50) {
                recos.push({
                    tipo: "expansion",
                    prioridad: "baja",
                    titulo: "Oportunidad de Mejora",
                    descripcion: `${sucursalDebil.nombre} vende ${porcentajeDif.toFixed(0)}% menos que ${sucursalFuerte.nombre}.`,
                    impacto: `Potencial de +${Math.round(diferencia * 0.3)} unidades mensuales`,
                    icon: "trending",
                    color: "purple",
                });
            }
        }

        setRecommendations(recos);
    };

    // GENERAR HEATMAP (Día de semana x Semana del año)
    const generateHeatmap = (data) => {
        const heatmap = {};

        data.forEach(item => {
            const date = item.fecha.toDate();
            const weekNumber = getWeekNumber(date);
            const dia = item.diaSemana || "LUNES";

            const key = `${weekNumber}-${dia}`;
            if (!heatmap[key]) {
                heatmap[key] = {
                    semana: weekNumber,
                    dia,
                    ventas: 0,
                    count: 0,
                };
            }

            heatmap[key].ventas += item.salidaTotal || 0;
            heatmap[key].count += 1;
        });

        const heatmapArray = Object.values(heatmap).map(h => ({
            ...h,
            promedio: h.count > 0 ? Math.round(h.ventas / h.count) : 0,
        }));

        setHeatmapData(heatmapArray);
    };

    // ENCONTRAR MEJORES Y PEORES DÍAS
    const findBestWorstDays = (data) => {
        const dias = data.map(item => ({
            fecha: item.fecha.toDate().toLocaleDateString("es-MX"),
            dia: item.diaSemana,
            ventas: item.salidaTotal || 0,
        }));

        const ordenados = dias.sort((a, b) => b.ventas - a.ventas);

        setBestWorstDays({
            best: ordenados.slice(0, 10),
            worst: ordenados.slice(-10).reverse(),
        });
    };

    // UTILIDADES
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    const calcularVariabilidad = (porDiaArray) => {
        if (!porDiaArray || porDiaArray.length === 0) return 0;

        const valores = porDiaArray.map(d => d.promedio);
        const promedio = valores.reduce((sum, v) => sum + v, 0) / valores.length;

        if (promedio === 0) return 0;

        const varianza = valores.reduce((sum, v) => sum + Math.pow(v - promedio, 2), 0) / valores.length;
        const desviacion = Math.sqrt(varianza);

        return ((desviacion / promedio) * 100).toFixed(1);
    };

    const exportToExcel = () => {
        try {
            // Hoja 1: Análisis por Día
            const diasSheet = weekdayAnalysis.map(d => ({
                "Día": d.dia,
                "Promedio Ventas": d.promedio,
                "Total Días": d.totalDias,
                "Total Ventas": d.totalVentas,
                "Devoluciones": d.devoluciones,
                "Tasa Devolución (%)": d.tasaDevolucion,
            }));

            // Hoja 2: Análisis por Sucursal
            const sucursalesSheet = branchAnalysis.map(s => ({
                "Sucursal": s.nombre,
                "Total Anual": s.total,
                "Mejor Día": s.mejorDia,
                "Peor Día": s.peorDia,
                "Variabilidad (%)": s.variabilidad,
            }));

            // Hoja 3: Recomendaciones
            const recosSheet = recommendations.map(r => ({
                "Tipo": r.tipo,
                "Prioridad": r.prioridad,
                "Título": r.titulo,
                "Descripción": r.descripcion,
                "Impacto": r.impacto,
            }));

            // Hoja 4: Mejores/Peores Días
            const mejoresDias = bestWorstDays.best.map(d => ({
                "Fecha": d.fecha,
                "Día Semana": d.dia,
                "Ventas": d.ventas,
            }));

            const wb = XLSX.utils.book_new();

            const ws1 = XLSX.utils.json_to_sheet(diasSheet);
            XLSX.utils.book_append_sheet(wb, ws1, "Por Día Semana");

            const ws2 = XLSX.utils.json_to_sheet(sucursalesSheet);
            XLSX.utils.book_append_sheet(wb, ws2, "Por Sucursal");

            const ws3 = XLSX.utils.json_to_sheet(recosSheet);
            XLSX.utils.book_append_sheet(wb, ws3, "Recomendaciones");

            const ws4 = XLSX.utils.json_to_sheet(mejoresDias);
            XLSX.utils.book_append_sheet(wb, ws4, "Top 10 Mejores Días");

            const excelBuffer = XLSX.write(wb, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const fileName = `Analisis_Avanzado_${selectedYear}.xlsx`;
            saveAs(blob, fileName);
        } catch (error) {
            console.error("Error exportando:", error);
        }
    };

    const getRecommendationIcon = (icon) => {
        switch (icon) {
            case "target": return <Target className="w-5 h-5" />;
            case "zap": return <Zap className="w-5 h-5" />;
            case "alert": return <AlertCircle className="w-5 h-5" />;
            case "calendar": return <Calendar className="w-5 h-5" />;
            case "trending": return <TrendingUp className="w-5 h-5" />;
            default: return <Lightbulb className="w-5 h-5" />;
        }
    };

    const getRecommendationColor = (color) => {
        const colors = {
            orange: "bg-orange-50 border-orange-200 text-orange-800",
            green: "bg-green-50 border-green-200 text-green-800",
            red: "bg-red-50 border-red-200 text-red-800",
            blue: "bg-blue-50 border-blue-200 text-blue-800",
            purple: "bg-purple-50 border-purple-200 text-purple-800",
        };
        return colors[color] || colors.blue;
    };

    const getPriorityColor = (prioridad) => {
        switch (prioridad) {
            case "alta": return "bg-red-100 text-red-800";
            case "media": return "bg-yellow-100 text-yellow-800";
            case "baja": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-50 rounded-xl">
                            <Activity className="w-8 h-8 text-indigo-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Análisis Avanzado
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Insights profundos y recomendaciones inteligentes
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setSelectedYear(selectedYear - 1)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="px-4 py-2 bg-gray-100 rounded-lg font-semibold text-gray-900">
                            {selectedYear}
                        </div>
                        <button
                            onClick={() => setSelectedYear(selectedYear + 1)}
                            disabled={selectedYear >= new Date().getFullYear()}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                            onClick={fetchYearData}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refrescar"
                        >
                            <RefreshCw className="w-5 h-5 text-gray-600" />
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            Exportar Análisis
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center p-12 bg-white rounded-2xl">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-600">Analizando datos...</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Recomendaciones Inteligentes */}
                    {recommendations.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <Lightbulb className="w-5 h-5 text-yellow-600" />
                                <h2 className="text-xl font-bold text-gray-900">
                                    Recomendaciones Inteligentes
                                </h2>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {recommendations.map((reco, index) => (
                                    <div
                                        key={index}
                                        className={`p-6 rounded-xl border-2 ${getRecommendationColor(reco.color)}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="p-2 bg-white rounded-lg">
                                                {getRecommendationIcon(reco.icon)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-semibold text-lg">
                                                        {reco.titulo}
                                                    </h3>
                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPriorityColor(reco.prioridad)}`}>
                                                        {reco.prioridad.toUpperCase()}
                                                    </span>
                                                </div>
                                                <p className="text-sm mb-2">
                                                    {reco.descripcion}
                                                </p>
                                                <p className="text-xs font-medium opacity-75">
                                                    💡 {reco.impacto}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Análisis por Día de la Semana */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <Clock className="w-5 h-5 text-blue-600" />
                            <h2 className="text-xl font-bold text-gray-900">
                                Análisis por Día de la Semana
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Gráfico de Barras */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-2">
                                    Ventas Promedio por Día (Salidas)
                                </h3>
                                <p className="text-xs text-gray-500 mb-4">
                                    🟢 Verde = Días con MÁS ventas | 🟠 Naranja = Días con MENOS ventas
                                </p>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={weekdayAnalysis}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="dia"
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                            tick={{ fontSize: 11 }}
                                        />
                                        <YAxis label={{ value: 'Unidades Vendidas', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} />
                                        <Tooltip
                                            formatter={(value) => [`${value.toLocaleString()} unidades`, 'Promedio de Ventas']}
                                        />
                                        <Bar dataKey="promedio" name="Promedio de Ventas">
                                            {weekdayAnalysis.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.promedio > (weekdayAnalysis.reduce((sum, d) => sum + d.promedio, 0) / 7) ? COLORS.success : COLORS.warning}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Tabla de Datos */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-700 mb-4">
                                    Desglose Detallado
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left font-medium text-gray-600">Día</th>
                                                <th className="px-4 py-2 text-right font-medium text-gray-600">Ventas Promedio</th>
                                                <th className="px-4 py-2 text-center font-medium text-gray-600">Performance</th>
                                                <th className="px-4 py-2 text-right font-medium text-gray-600">Tasa Dev.</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {weekdayAnalysis
                                                .sort((a, b) => b.promedio - a.promedio)
                                                .map((dia, index) => {
                                                    const promedioGeneral = weekdayAnalysis.reduce((sum, d) => sum + d.promedio, 0) / 7;
                                                    const esMejor = dia.promedio > promedioGeneral * 1.2;
                                                    const esPeor = dia.promedio < promedioGeneral * 0.8;

                                                    return (
                                                        <tr key={dia.dia} className="hover:bg-gray-50">
                                                            <td className="px-4 py-3 font-medium text-gray-900">
                                                                {dia.dia}
                                                                {index === 0 && <span className="ml-2 text-yellow-500">👑</span>}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-gray-900 font-semibold">
                                                                {dia.promedio.toLocaleString()}
                                                            </td>
                                                            <td className="px-4 py-3 text-center">
                                                                {esMejor && (
                                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                        ⚡ DÍA FUERTE
                                                                    </span>
                                                                )}
                                                                {esPeor && (
                                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                                                        🎯 PROMOCIONAR
                                                                    </span>
                                                                )}
                                                                {!esMejor && !esPeor && (
                                                                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                                                        📊 Normal
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3 text-right">
                                                                <span className={`px-2 py-1 rounded text-xs font-medium ${dia.tasaDevolucion < 3
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : dia.tasaDevolucion < 5
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {dia.tasaDevolucion}%
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <p className="text-xs text-blue-800">
                                        <strong>📊 Interpretación:</strong> <span className="text-green-600 font-semibold">Días Fuertes</span> = MÁS salidas (mejores ventas). <span className="text-orange-600 font-semibold">Promocionar</span> = MENOS salidas (oportunidad de crecimiento).
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Análisis Estacional */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <Calendar className="w-5 h-5 text-purple-600" />
                            <h2 className="text-xl font-bold text-gray-900">
                                Análisis Estacional (Por Semanas)
                            </h2>
                        </div>

                        <ResponsiveContainer width="100%" height={300}>
                            <ComposedChart data={seasonalAnalysis}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="semana" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="ventas"
                                    fill={COLORS.info}
                                    fillOpacity={0.3}
                                    stroke={COLORS.info}
                                    name="Ventas Totales"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="promedio"
                                    stroke={COLORS.primary}
                                    strokeWidth={2}
                                    name="Promedio Diario"
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Análisis por Sucursal */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <Users className="w-5 h-5 text-green-600" />
                            <h2 className="text-xl font-bold text-gray-900">
                                Performance por Sucursal
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {branchAnalysis.slice(0, 6).map((branch) => (
                                <div
                                    key={branch.nombre}
                                    className="border border-gray-200 rounded-xl p-4"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-semibold text-gray-900">
                                            {branch.nombre}
                                        </h3>
                                        <span className="text-xs font-medium text-gray-500">
                                            Variabilidad: {branch.variabilidad}%
                                        </span>
                                    </div>

                                    <ResponsiveContainer width="100%" height={200}>
                                        <RadarChart data={branch.porDia}>
                                            <PolarGrid />
                                            <PolarAngleAxis
                                                dataKey="dia"
                                                tick={{ fontSize: 10 }}
                                            />
                                            <PolarRadiusAxis />
                                            <Radar
                                                name={branch.nombre}
                                                dataKey="promedio"
                                                stroke={COLORS.primary}
                                                fill={COLORS.primary}
                                                fillOpacity={0.6}
                                            />
                                            <Tooltip />
                                        </RadarChart>
                                    </ResponsiveContainer>

                                    <div className="mt-4 flex justify-between text-xs">
                                        <div>
                                            <span className="text-gray-500">Mejor día:</span>
                                            <span className="ml-1 font-semibold text-green-600">
                                                {branch.mejorDia}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Peor día:</span>
                                            <span className="ml-1 font-semibold text-red-600">
                                                {branch.peorDia}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Top 10 Mejores/Peores Días */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Mejores Días */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="w-5 h-5 text-yellow-600" />
                                <h2 className="text-xl font-bold text-gray-900">
                                    Top 10 Mejores Días
                                </h2>
                            </div>
                            <div className="space-y-2">
                                {bestWorstDays.best.map((dia, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg font-bold text-green-600">
                                                #{index + 1}
                                            </span>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {dia.fecha}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {dia.dia}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-lg font-bold text-green-600">
                                            {dia.ventas.toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Peores Días */}
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <Target className="w-5 h-5 text-orange-600" />
                                <h2 className="text-xl font-bold text-gray-900">
                                    10 Días con Menos Ventas
                                </h2>
                            </div>
                            <div className="space-y-2">
                                {bestWorstDays.worst.map((dia, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg font-bold text-orange-600">
                                                #{index + 1}
                                            </span>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {dia.fecha}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {dia.dia}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-lg font-bold text-orange-600">
                                            {dia.ventas.toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnalisisAvanzado;

